import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect, type ReactNode } from 'react';

/**
 * Gates an app behind a signed-in account. Nothing inside renders until MSAL has one, which
 * also keeps token-consuming providers from mounting and throwing "No signed-in account".
 *
 * Defaults to an explicit sign-in button rather than redirecting on load: an automatic
 * redirect makes a misconfigured tenant look like a hang, and bounces anyone who lands on
 * the app by accident. Pass autoSignIn to opt into the redirect.
 *
 * `fallback` replaces the built-in card for an app that wants a real unauthenticated screen —
 * player-web's landing page is the one caller. It is a render prop taking the sign-in call
 * rather than a component, which is what keeps `loginRedirect`'s idle guard below in one place:
 * a screen holding its own MSAL instance would have to repeat that check, and the failure of
 * forgetting is a rejected promise on a double click rather than anything visible.
 *
 * The transient "Signing in…" state is deliberately *not* delegated. It covers the moment MSAL
 * is handling a redirect response, and a landing page rendering there would flash the door back
 * up at somebody who has just come through it.
 */
export function RequireAuth({
  children,
  appName,
  scopes = [],
  autoSignIn = false,
  variant = 'light',
  fallback,
}: {
  children: ReactNode;
  appName: string;
  scopes?: string[];
  autoSignIn?: boolean;
  variant?: 'light' | 'dark';
  fallback?: (signIn: () => void) => ReactNode;
}) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const scopeKey = scopes.join(' ');
  const idle = inProgress === InteractionStatus.None;

  useEffect(() => {
    if (!autoSignIn || isAuthenticated || !idle) return;
    // loginRedirect rejects while another interaction is in flight, hence the idle check.
    void instance.loginRedirect({ scopes: scopeKey ? scopeKey.split(' ') : [] });
  }, [autoSignIn, isAuthenticated, idle, instance, scopeKey]);

  if (isAuthenticated) return <>{children}</>;

  const signIn = () => {
    void instance.loginRedirect({ scopes: scopeKey ? scopeKey.split(' ') : [] });
  };

  const dark = variant === 'dark';
  const shell = dark
    ? 'grid min-h-screen place-items-center bg-ink-950 p-6 text-white'
    : 'grid min-h-screen place-items-center bg-felt-50/40 p-6';
  const muted = dark ? 'text-white/60' : 'text-ink-800/60';

  if (!idle) {
    return (
      <div className={shell}>
        <p className={`text-sm ${muted}`}>Signing in…</p>
      </div>
    );
  }

  if (fallback) return <>{fallback(signIn)}</>;

  return (
    <div className={shell}>
      <div className="w-full max-w-sm text-center">
        <h1 className={dark ? 'font-display text-xl font-semibold' : 'font-display text-xl font-semibold text-ink-950'}>
          {appName}
        </h1>
        <p className={`mt-1 text-sm ${muted}`}>Sign in with your organisation account to continue.</p>
        <button
          type="button"
          onClick={signIn}
          className="mt-6 w-full rounded-lg bg-felt-700 py-2.5 text-sm font-medium text-white transition hover:bg-felt-900"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
